import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Flex, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import styled from "styled-components";

import { analytics } from "MVVM/Analytics";
import { ESupportedGames } from "MVVM/@types";
import { MatchHistoryMatchThumbnailState } from "MVVM/State/matchHistory";
import { URLS } from "MVVM/Utilities/Maps";
import { DynamicSignupOptions } from "MVVM/Views/Authentication/Components/DynamicSignupOptions";
import { TMoment } from "MVVM/Views/MatchHistory/@types";
import { StyledMomentIcon } from "MVVM/Views/MatchHistory/Components/Shared/Match/styledComponents";
import { TinyAvatar } from "MVVM/Views/MatchHistory/styledComponents";

export const MatchHistoryLoginModal = ({
  game,
  moment,
}: {
  game: ESupportedGames;
  moment: TMoment;
}) => {
  const history = useHistory();
  const matchThumbnail = useRecoilValue(MatchHistoryMatchThumbnailState);
  const { t } = useTranslation();

  return (
    <Container>
      <ChampionBanner
        style={{
          backgroundImage: `linear-gradient(to bottom, transparent, var(--Gray-750, #1c2a34)), url(${matchThumbnail})`,
        }}
      />
      <MomentDetails moment={moment} />
      <Flex
        style={{ padding: "12px 24px 24px", gap: "24px" }}
        flexDirection="column"
      >
        <Flex flexDirection="column" textAlign="center" style={{ gap: "12px" }}>
          <Text color="ice" variant="text.titleSmall">
            {t("matchHistoryLoginModal.title", "Don't Miss Out On Your Play!")}
          </Text>
          <Text color="chalk" variant="text.paragraphMedium">
            {t(
              "matchHistoryLoginModal.subtitle",
              "Create a FREE Allstar.gg account to create, edit, and share a clip from this moment.",
            )}
          </Text>
        </Flex>
        <DynamicSignupOptions game={game} renderCopy={false} />
        <Flex style={{ gap: 10 }} justifyContent="center">
          <Text color="chalk" variant={"text.labelMedium"}>
            {t("signup.alreadyAMember", "Already a member?")}
          </Text>
          <LoginText
            variant={"text.labelMedium"}
            color="envy"
            mr={4}
            onClick={() => {
              analytics.track("Match History - Login Modal", {
                type: "signup",
                action: "click",
              });
              const nextLink = window.location.search
                ? `${window.location.pathname}${window.location.search}`
                : window.location.pathname;
              history.push(`${URLS.LOGIN}?next=${nextLink}`);
            }}
          >
            {t("oauth.login", "Log in")}
          </LoginText>
        </Flex>
      </Flex>
    </Container>
  );
};

export const MomentDetails = ({
  moment,
  hideTimelineLabel,
}: {
  moment: TMoment;
  hideTimelineLabel?: boolean;
}) => {
  return (
    <DetailsContainer>
      {!hideTimelineLabel && (
        <Text variant="text.labelXSmall">{moment.timelineLabel}</Text>
      )}
      <Players>
        <TinyAvatar src={moment.player.avatar} />
        <StyledMomentIcon src={moment.icons[0]} className="hide-on-mobile" />
        {moment.victimData.map((victim) => (
          <TinyAvatar src={victim.avatar} className="hide-on-mobile" />
        ))}
      </Players>
      <MomentTitle>
        <Text
          variant="text.labelSmall"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {moment.title}
        </Text>
      </MomentTitle>
    </DetailsContainer>
  );
};

const DetailsContainer = styled(Flex)`
  align-items: center;
  background: ${(props) => props.theme.colors.midnightAlpha70};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  flex-wrap: wrap-reverse;
  gap: 24px;
  height: auto;
  justify-content: center;
  left: 22px;
  padding: 8px 12px 8px 16px;
  position: absolute;
  top: 110px;
  width: 90%;
`;

const Players = styled(Flex)`
  align-items: center;
  gap: 8px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    .hide-on-mobile {
      display: none;
    }
  }
`;

const MomentTitle = styled(Flex)`
  align-items: center;
`;

const Container = styled(Flex)`
  position: relative;
  width: 448px;
  flex-direction: column;
  background-color: var(--Gray-750, #1c2a34);
  text-align: center;
  border-radius: 14px;
  border: ${({ theme }) => `1px solid ${theme.colors.envyAlpha20}`};
  box-shadow: ${({ theme }) => `0px 0px 16px 0px ${theme.colors.envyAlpha20}`};
`;

const ChampionBanner = styled(Flex)`
  background-size: cover;
  height: 300px;
  width: 100%;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
`;

const LoginText = styled(Text)`
  cursor: pointer;
`;
